import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import exact from 'prop-types-exact'
import { Modal } from 'lp-components'
import { TimeoutModal, Tooltip, PlanEinTooltipContent } from 'components'
import { displayEin, displayAttribute } from 'utils'
import { UploadSignedPlanAgreementForm } from '../forms'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { flashErrorMessage } from 'redux-flash'
import { selectors as globalSelectors } from 'global-reducer'
import * as apiActions from 'api-actions'
import * as globalActions from '../../actions'
import {
  SERVICES_AGREEMENT_UPLOAD_INSTRUCTIONS,
  ADDENDUM_UPLOAD_INSTRUCTIONS,
  GrandfatheredUserValue,
  GFServicesAgreementSubmissionType,
} from 'config/automatic-rollovers'
import {
  TAXABLE_ACCOUNTS_AGREEMENT_UPLOAD_INSTRUCTIONS,
  TAXABLE_ACCOUNTS_INITIAL_INVESTMENT_TOOLTIP_CONTENT,
} from 'config/taxable-accounts'
import {
  TOAST_ERROR_CONTACT_SUPPORT,
  VIRUS_FILE_ERROR_MESSAGE,
  UPLOAD_AGREEMENT_SUCCESS_MESSAGE,
  INITIAL_INVESTMENT_TOOLTIP_CONTENT,
  COMPLETE_LEGAL_PLAN_NAME_TOOLTIP_CONTENT,
} from 'config/notifications'
import { ProductAgreementType } from 'config/products'
import { Icon as InfoIcon } from 'images/info.svg'
import { isGfBDAgreementsEnabled } from 'config/portal'

const propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  servicesAgreement: PropTypes.oneOfType([
    Types.servicesAgreementSummary,
    Types.servicesAgreementShow,
  ]).isRequired,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  submitServicesAgreementFile: PropTypes.func.isRequired,
  submitTaxableServicesAgreementFile: PropTypes.func.isRequired,
  submitBDServicesAgreementFile: PropTypes.func.isRequired,
  showSuccessModal: PropTypes.func.isRequired,
  agreementsType: PropTypes.string.isRequired,
  currentUser: Types.user.isRequired,
}

const defaultProps = {}

function UploadPlanAgreementModal({
  onClose,
  servicesAgreement,
  onSubmitSuccess,
  flashErrorMessageHandler,
  submitServicesAgreementFile,
  submitTaxableServicesAgreementFile,
  submitBDServicesAgreementFile,
  showSuccessModal,
  agreementsType,
  currentUser,
}) {
  const [hasTimedOut, setHasTimedOut] = useState(false)
  const isGrandfatheredBDUser =
    agreementsType === ProductAgreementType.BD &&
    currentUser?.grandfatheredUser === GrandfatheredUserValue &&
    isGfBDAgreementsEnabled()
  const handleSubmit = async (
    { signedAgreement },
    { setFieldError, setFieldValue }
  ) => {
    try {
      const formData = new FormData()
      formData.append('submitFile', signedAgreement, signedAgreement.name)
      formData.append(
        'submitData',
        JSON.stringify({
          serviceAgreementID:
            agreementsType === ProductAgreementType.ARO ||
            agreementsType === ProductAgreementType.TAXABLE_ACCOUNTS
              ? servicesAgreement.serviceAgreementID
              : servicesAgreement?.palPlanId,
          planName:
            agreementsType === ProductAgreementType.ARO ||
            agreementsType === ProductAgreementType.TAXABLE_ACCOUNTS
              ? servicesAgreement.name
              : servicesAgreement.completeLegalPlanName,
          planEIN:
            agreementsType === ProductAgreementType.ARO ||
            agreementsType === ProductAgreementType.TAXABLE_ACCOUNTS
              ? servicesAgreement.planEIN
              : servicesAgreement.ein,
          // to-do: add agreementTemplateID to formData once logic to conditionally render agreement type field is implemented
        })
      )
      if (agreementsType === ProductAgreementType.ARO) {
        await submitServicesAgreementFile({
          planAgreementId: servicesAgreement.serviceAgreementID,
          formData,
        })
      } else if (agreementsType === ProductAgreementType.TAXABLE_ACCOUNTS) {
        await submitTaxableServicesAgreementFile({
          formData,
        })
      } else {
        await submitBDServicesAgreementFile({
          planAgreementId: servicesAgreement.palPlanId,
          formData,
          status: servicesAgreement.addendumStatus,
          ...(isGrandfatheredBDUser &&
            servicesAgreement.agreementStatus && {
              submissionType:
                GFServicesAgreementSubmissionType.SIGNED_AGREEMENT_FILE,
            }),
        })
      }
      showSuccessModal({
        title: 'Request Received',
        message: UPLOAD_AGREEMENT_SUCCESS_MESSAGE,
      })
      onSubmitSuccess()
    } catch (e) {
      if (e.status === 400) {
        setFieldValue('signedAgreement', '', false)
        setFieldError('signedAgreement', VIRUS_FILE_ERROR_MESSAGE)
      } else if (e.status === 504) {
        setHasTimedOut(true)
      } else {
        flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT)
      }
    }
  }

  if (hasTimedOut) {
    return <TimeoutModal onClose={onClose} />
  }

  const getServicesAgreementsUploadInstructions = () => {
    if (agreementsType === ProductAgreementType.ARO) {
      return SERVICES_AGREEMENT_UPLOAD_INSTRUCTIONS
    } else if (agreementsType === ProductAgreementType.TAXABLE_ACCOUNTS) {
      return TAXABLE_ACCOUNTS_AGREEMENT_UPLOAD_INSTRUCTIONS
    } else {
      return ADDENDUM_UPLOAD_INSTRUCTIONS
    }
  }

  return (
    <Modal id="upload-services-agreement-modal" onClose={onClose}>
      <h1>Upload your document</h1>
      <p>{getServicesAgreementsUploadInstructions()}</p>
      <dl className="plan-details-container">
        <div className="plan-details-column">
          <dt>
            Complete legal plan name:
            <Tooltip
              content={COMPLETE_LEGAL_PLAN_NAME_TOOLTIP_CONTENT}
              ariaLabel="Complete legal plan name information"
            >
              <InfoIcon className="icon-info" />
            </Tooltip>
          </dt>
          {agreementsType === ProductAgreementType.ARO ||
          agreementsType === ProductAgreementType.TAXABLE_ACCOUNTS ? (
            <dd>{displayAttribute(servicesAgreement.name)}</dd>
          ) : (
            <dd>{displayAttribute(servicesAgreement.completeLegalPlanName)}</dd>
          )}
        </div>
        <div className="plan-details-column">
          <dt>
            Employer Identification Number (EIN):
            <Tooltip
              content={<PlanEinTooltipContent />}
              ariaLabel="Plan EIN information"
            >
              <InfoIcon className="icon-info" />
            </Tooltip>
          </dt>
          {agreementsType === ProductAgreementType.ARO ||
          agreementsType === ProductAgreementType.TAXABLE_ACCOUNTS ? (
            <dd>{displayEin(servicesAgreement.planEIN)}</dd>
          ) : (
            <dd>{displayEin(servicesAgreement.ein)}</dd>
          )}
        </div>
        {(agreementsType === ProductAgreementType.ARO ||
          agreementsType === ProductAgreementType.TAXABLE_ACCOUNTS) && (
          <div className="plan-details-column">
            <dt>
              Initial investment:
              <Tooltip
                content={
                  agreementsType === ProductAgreementType.ARO
                    ? INITIAL_INVESTMENT_TOOLTIP_CONTENT
                    : TAXABLE_ACCOUNTS_INITIAL_INVESTMENT_TOOLTIP_CONTENT
                }
                ariaLabel="Initial investment information"
              >
                <InfoIcon className="icon-info" />
              </Tooltip>
            </dt>
            <dd>{displayAttribute(servicesAgreement.portalAgreementType)}</dd>
          </div>
        )}
      </dl>
      <UploadSignedPlanAgreementForm
        handleSubmit={handleSubmit}
        handleCancel={onClose}
      />
    </Modal>
  )
}

function mapStateToProps(state) {
  return {
    currentUser: globalSelectors.currentUser(state),
  }
}

const mapDispatchToProps = {
  showSuccessModal: globalActions.showSuccessModal,
  flashErrorMessageHandler: flashErrorMessage,
  submitServicesAgreementFile: apiActions.submitServicesAgreementFile,
  submitTaxableServicesAgreementFile:
    apiActions.submitTaxableServicesAgreementFile,
  submitBDServicesAgreementFile: apiActions.submitBDServicesAgreementFile,
}

UploadPlanAgreementModal.propTypes = exact(propTypes)
UploadPlanAgreementModal.defaultProps = defaultProps

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  UploadPlanAgreementModal
)
